import { useEffect, useState } from "react";
import api, { fmtMoney, fmtDate } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle, ArrowsClockwise, Gear } from "@phosphor-icons/react";
import CuentaCombobox from "@/components/CuentaCombobox";

const UNIDADES = ["Mostrador", "Logistica", "Mayorista", "Ajustes"];

export default function ComprasAClasificarPage() {
  const [rows, setRows] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [rowCuenta, setRowCuenta] = useState({});
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  // Configuración cuenta de ingreso por unidad de negocio
  const [unMapping, setUnMapping] = useState({});
  const [savingCfg, setSavingCfg] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: c }, { data: cu }, { data: cfg }] = await Promise.all([
        api.get("/contabilidad/compras-a-clasificar"),
        api.get("/cuentas"),
        api.get("/contabilidad/config/unidades-negocio"),
      ]);
      setRows(c);
      setCuentas(cu);
      setUnMapping(cfg?.mapping || {});
    } finally {
      setLoading(false);
    }
  };

  const cuentasGasto = (cuentas || []).filter((x) => x.imputable && (x.tipo === "egreso" || x.tipo === "activo"));
  const cuentasIngreso = (cuentas || []).filter((x) => x.imputable && x.tipo === "ingreso");

  useEffect(() => { load(); }, []);

  const clasificar = async (cid) => {
    const codigo = rowCuenta[cid];
    if (!codigo) return toast.error("Seleccioná una cuenta contable");
    try {
      await api.post(`/contabilidad/compras-a-clasificar/${cid}`, { cuenta_codigo: codigo });
      toast.success("Compra clasificada y asiento generado");
      setRowCuenta({ ...rowCuenta, [cid]: undefined });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const runBackfill = async (force = false) => {
    const msg = force
      ? "Force: además de generar faltantes, va a BORRAR y REGENERAR los asientos automáticos existentes de ventas/compras (productos o con cuenta default)/cobros/pagos con la lógica actualizada.\n\nLos asientos de compras clasificadas manualmente se preservan.\n\n¿Continuar?"
      : "Reprocesar todo el histórico y generar asientos faltantes de ventas, compras (productos) y cobros/pagos con caja asignada. ¿Continuar?";
    if (!window.confirm(msg)) return;
    setRunning(true);
    try {
      const { data } = await api.post(`/contabilidad/backfill-asientos${force ? "?force=true" : ""}`);
      const s = data.stats || {};
      toast.success(
        `Backfill OK: v=${s.ventas || 0} c=${s.compras || 0} co=${s.cobros || 0} pa=${s.pagos || 0}${force ? ` · regen=${s.regenerados || 0}` : ""}`
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error en backfill");
    } finally {
      setRunning(false);
    }
  };

  const saveUnMapping = async () => {
    setSavingCfg(true);
    try {
      await api.put("/contabilidad/config/unidades-negocio", { mapping: unMapping });
      toast.success("Cuentas por unidad de negocio guardadas");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally {
      setSavingCfg(false);
    }
  };

  return (
    <>
      <Topbar
        title="Compras a Clasificar"
        subtitle="Compras de proveedores de servicios/insumos pendientes de imputar en el plan contable."
        right={
          <div className="flex items-center gap-2">
            <Button
              onClick={() => runBackfill(false)}
              disabled={running}
              variant="outline"
              className="rounded-sm h-9 border-[#6B1F24] text-[#6B1F24] hover:bg-[#F5F1E9]"
              data-testid="backfill-asientos-btn"
            >
              <ArrowsClockwise size={16} className="mr-1" />
              {running ? "Procesando…" : "Generar faltantes"}
            </Button>
            <Button
              onClick={() => runBackfill(true)}
              disabled={running}
              className="rounded-sm h-9 bg-[#6B1F24] hover:bg-[#571519]"
              data-testid="backfill-force-btn"
            >
              <ArrowsClockwise size={16} className="mr-1" />
              Reasentar todo (force)
            </Button>
          </div>
        }
      />
      <div className="p-4 space-y-4">
        {/* Panel: Cuenta de ingreso por unidad de negocio */}
        <div className="border border-[#E5E5E5] bg-white rounded-sm overflow-hidden">
          <div className="px-4 py-3 border-b bg-neutral-50 flex items-center gap-2">
            <Gear size={16} className="text-[#6B1F24]" />
            <span className="text-[11px] uppercase tracking-widest font-mono text-[#6B1F24] font-semibold">
              Cuenta de ingreso por unidad de negocio
            </span>
            <span className="text-[11px] text-neutral-500 ml-auto">
              Fallback usado si el cliente no tiene una cuenta específica
            </span>
          </div>
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {UNIDADES.map((un) => (
              <div key={un}>
                <div className="text-[10px] uppercase tracking-widest font-mono text-neutral-500 mb-1">
                  {un}
                </div>
                <CuentaCombobox
                  value={unMapping[un] || ""}
                  onChange={(codigo) => setUnMapping({ ...unMapping, [un]: codigo })}
                  cuentas={cuentasIngreso}
                  placeholder="4.1.1 Venta de Bienes (default)"
                  testid={`cfg-un-${un}`}
                  compact
                />
              </div>
            ))}
          </div>
          <div className="px-4 py-2 border-t bg-neutral-50 flex justify-end">
            <Button
              size="sm"
              onClick={saveUnMapping}
              disabled={savingCfg}
              className="rounded-sm h-8 bg-[#6B1F24] hover:bg-[#571519]"
              data-testid="cfg-un-save"
            >
              {savingCfg ? "Guardando…" : "Guardar configuración"}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-neutral-500 text-sm">Cargando…</div>
        ) : rows.length === 0 ? (
          <div className="border border-neutral-200 bg-white rounded-sm p-8 text-center">
            <CheckCircle size={32} weight="fill" className="text-emerald-500 mx-auto mb-2" />
            <div className="font-heading text-lg">Todo al día</div>
            <div className="text-[13px] text-neutral-500 mt-1">
              No hay compras de servicios/insumos pendientes de clasificar.
            </div>
          </div>
        ) : (
          <div className="border border-[#E5E5E5] bg-white rounded-sm overflow-hidden">
            <table className="w-full text-[13px]">
              <thead className="text-[11px] uppercase font-mono text-[#666] bg-neutral-50">
                <tr>
                  <th className="px-4 py-2 text-left w-24">Fecha</th>
                  <th className="px-4 py-2 text-left w-40">Comprobante</th>
                  <th className="px-4 py-2 text-left">Proveedor</th>
                  <th className="px-4 py-2 text-right w-36">Total</th>
                  <th className="px-4 py-2 text-left w-72">Cuenta contable</th>
                  <th className="px-4 py-2 text-left w-28"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t hover:bg-neutral-50" data-testid={`clasif-row-${r.id}`}>
                    <td className="px-4 py-2 font-mono">{fmtDate(r.fecha)}</td>
                    <td className="px-4 py-2 font-mono text-[12px]">
                      {r.tipo_comprobante} {r.numero}
                    </td>
                    <td className="px-4 py-2">{r.contraparte_nombre}</td>
                    <td className="px-4 py-2 text-right font-mono">{fmtMoney(r.total, r.moneda)}</td>
                    <td className="px-4 py-2">
                      <CuentaCombobox
                        value={rowCuenta[r.id] || ""}
                        onChange={(codigo) => setRowCuenta({ ...rowCuenta, [r.id]: codigo })}
                        cuentas={cuentasGasto}
                        compact
                        testid={`clasif-cuenta-${r.id}`}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <Button
                        size="sm"
                        disabled={!rowCuenta[r.id]}
                        onClick={() => clasificar(r.id)}
                        className="rounded-sm h-8 bg-[#6B1F24] hover:bg-[#571519]"
                        data-testid={`clasif-btn-${r.id}`}
                      >
                        Clasificar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
